import React, { useCallback, useEffect, useState } from 'react';
import PolarisAdminSelect from './PolarisAdminSelect';
import {
    createPolarisInterest,
    fetchPolarisInterestCatalog,
    InterestCategoryAdminItem,
    updatePolarisInterest,
} from '../services/polarisService';
import '../styles/polaris-admin.css';

export default function InterestsManagementPanel() {
    const [categories, setCategories] = useState<InterestCategoryAdminItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | ''>('');
    const [newInterestName, setNewInterestName] = useState('');
    const [saving, setSaving] = useState(false);
    const [actionInterestId, setActionInterestId] = useState<number | null>(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const data = await fetchPolarisInterestCatalog();
            setCategories(data);
            if (data.length > 0) {
                setSelectedCategoryId((current) => current || data[0].id);
            }
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to load interests.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleCreateInterest = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selectedCategoryId || !newInterestName.trim()) {
            setError('Choose a category and enter an interest name.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await createPolarisInterest(Number(selectedCategoryId), newInterestName.trim());
            setNewInterestName('');
            await load();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to create interest.');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (interestId: number, active: boolean) => {
        setActionInterestId(interestId);
        setError(null);
        try {
            await updatePolarisInterest(interestId, { active });
            await load();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to update interest.');
        } finally {
            setActionInterestId(null);
        }
    };

    return (
        <div data-testid="polaris-interests-panel">
            <p style={{ color: '#6b7280', margin: '0 0 1.5rem' }}>
                Manage media kit interests. Hiding an interest removes it from athlete pickers but keeps existing athlete selections intact.
            </p>

            {error && <div className="pa-error">{error}</div>}
            {loading && <div className="pa-loading">Loading interests…</div>}

            {!loading && (
                <>
                    <div className="pa-metric-card pa-metric-card-full" style={{ marginBottom: 24 }}>
                        <div className="pa-metric-card-title">Add Interest</div>
                        <form
                            style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}
                            onSubmit={handleCreateInterest}
                        >
                            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                <span>Category</span>
                                <PolarisAdminSelect
                                    size="compact"
                                    value={selectedCategoryId === '' ? '' : String(selectedCategoryId)}
                                    placeholder="Select category"
                                    options={categories.map((category) => ({
                                        value: String(category.id),
                                        label: category.name,
                                    }))}
                                    onChange={(next) =>
                                        setSelectedCategoryId(next ? Number(next) : '')
                                    }
                                    data-testid="polaris-reference-data-category"
                                />
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                <span>Interest name</span>
                                <input
                                    className="pa-filter-input"
                                    value={newInterestName}
                                    onChange={(e) => setNewInterestName(e.target.value)}
                                    placeholder="e.g. Coding"
                                    data-testid="polaris-reference-data-interest-name"
                                />
                            </label>
                            <button
                                type="submit"
                                className="pa-page-btn"
                                disabled={saving}
                                data-testid="polaris-reference-data-add-interest"
                            >
                                {saving ? 'Adding…' : 'Add Interest'}
                            </button>
                        </form>
                    </div>

                    {categories.map((category) => (
                        <div key={category.id} className="pa-metric-card pa-metric-card-full" style={{ marginBottom: 24 }}>
                            <div className="pa-metric-card-title">{category.name}</div>
                            <table className="pa-table">
                                <thead>
                                    <tr>
                                        <th>Interest</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {category.interests.length === 0 && (
                                        <tr>
                                            <td colSpan={3}>No interests in this category yet.</td>
                                        </tr>
                                    )}
                                    {category.interests.map((interest) => (
                                        <tr key={interest.id} data-testid={`polaris-interest-row-${interest.id}`}>
                                            <td>{interest.name}</td>
                                            <td>
                                                <span className={`pa-badge ${interest.active ? 'pa-badge-active' : 'pa-badge-inactive'}`}>
                                                    {interest.active ? 'Visible' : 'Hidden'}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="pa-page-btn"
                                                    disabled={actionInterestId === interest.id}
                                                    onClick={() => handleToggleActive(interest.id, !interest.active)}
                                                    data-testid={`polaris-interest-toggle-${interest.id}`}
                                                >
                                                    {actionInterestId === interest.id
                                                        ? 'Saving…'
                                                        : interest.active
                                                            ? 'Hide'
                                                            : 'Show'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}
